import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import { AiGradientButton } from '../../shared';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import GuestDetailsStep from '../steps/GuestDetailsStep';
import ReviewStep from '../steps/ReviewStep';
import WalkInAiCheckinOverlay from '../WalkInAiCheckinOverlay';
import BookingConfirmedActionsContent from '../booking/BookingConfirmedActionsContent';
import CompanyDetailsHeadingActions from '../booking/CompanyDetailsHeadingActions';
import { computeAmountDueForPaymentLink } from '../../utils/bookingPaymentLink';
import { useBookingPaymentLinkComm } from '../../hooks/useBookingPaymentLinkComm';
import AlertDialog from '../common/AlertDialog';
import {
  getHotelRatePlans,
  getRatesByRatePlan,
  submitWalkIn,
  downloadBookingInvoice,
  downloadBookingConfirmation,
  sendBookingConfirmation,
  getTaxRulesAvailability,
  searchGuestByPhone,
  getCustomerDetail,
} from '../../services/api';
import { formatWhatsAppSuccessMessage } from '../../utils/whatsappMessaging';
import countriesData from '../../data/countries-states-cities-data';
import countryCodesData from '../../data/country-codes.json';
import { countryFilterOptions, stateFilterOptions, cityFilterOptions } from '../../utils/locationSearch';
import { isValidPhoneForCountry } from '../../utils/phoneValidation';
import { calculateBookingPriceBreakdown } from '../../utils/bookingPriceCalculation';
import { isAutoGstEnabled } from '../../utils/autoGst';
import { PERMISSIONS } from '../../auth/permissions';
import usePermission from '../../auth/usePermission';
import { toPaymentProcessedAtIso } from '../../utils/dateUtils';
import {
  normalizeDocumentIdForSubmit,
  normalizeDocumentIdInput,
} from '../../utils/documentIdFormatters';
import { getBookingCurrencySymbol } from '../../utils/bookingPaymentSummary';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerPanelSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerAuditButtonSx,
  getBookingDrawerInputBg,
  getBookingDrawerDividerColor,
  BOOKING_DRAWER_ACCENT,
  QUICK_BOOKING_DRAWER_WIDTH_SX,
  getReservationOutlinedFieldSx,
  createBookingDrawerOverlayProps,
} from '../../utils/bookingDrawerLayout';
import { getDrawerRootSx } from '../../utils/drawerOverlayProps';
import { DASHBOARD_ACCENT } from '../../utils/dashboardSurfaceSx';
import {
  EMPTY_FOREIGN_GUEST_DETAILS,
  foreignDetailsToGuestApiPayload,
  isForeignGuestCountry,
} from '../../utils/foreignGuestFormUtils';

const VALID_DIAL_CODES = new Set(countryCodesData.map((cc) => cc.dialCode));

const EMPTY_COMPANY = {
  taxIdType: 'gstin',
  taxIdNumber: '',
  companyName: '',
  companyPhone: '',
  companyEmail: '',
  companyAddress: '',
  companyCity: '',
  companyState: '',
  companyCountry: '',
  companyPincode: '',
};

const hasAnyCompanyFieldValues = (details) =>
  Boolean(
    details &&
      (details.taxIdNumber ||
        details.companyName ||
        details.companyPhone ||
        details.companyEmail ||
        details.companyAddress ||
        details.companyCity ||
        details.companyState ||
        details.companyCountry ||
        details.companyPincode)
  );

const isCompanyFormEffectivelyEmpty = (details) => !hasAnyCompanyFieldValues(details);

const toPositiveInt = (raw, fallback) => {
  const parsed = Number.parseInt(String(raw ?? ''), 10);
  const normalized = Number.isFinite(parsed) ? Math.floor(parsed) : fallback;
  return normalized > 0 ? normalized : fallback;
};

const toCurrencyAmount = (value) => Math.round((Number(value) || 0) * 100) / 100;
const getSelectedHotelCurrency = () =>
  (typeof window !== 'undefined' && window.DJANGO_CONTEXT?.selectedHotel?.currency) || 'INR';

const resolveRoomTypeLimits = (roomTypeMeta = {}) => {
  const baseAdults = toPositiveInt(roomTypeMeta.base_adults, 1);
  const baseChildren = toPositiveInt(roomTypeMeta.base_children, 0);
  const capacity = toPositiveInt(roomTypeMeta.capacity, 0);
  const maxGuests = toPositiveInt(
    roomTypeMeta.max_guests,
    capacity > 0 ? capacity : Math.max(1, baseAdults + baseChildren)
  );
  return { baseAdults, baseChildren, maxGuests };
};

const generateIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const randomNibble = Math.floor(Math.random() * 16);
    const value = char === 'x' ? randomNibble : (randomNibble & 0x3) | 0x8;
    return value.toString(16);
  });
};

const corporateTaxIdOptions = [
  { value: 'gstin', label: 'GSTIN' },
  { value: 'pan', label: 'PAN' },
  { value: 'tan', label: 'TAN' },
  { value: 'cin', label: 'CIN' },
  { value: 'other', label: 'Other' },
];

/** PMS `/rates-by-meal-plan/` payload — use final_rate (resolved daily rate), then base_rate. */
function nightlyRateFromRatesPayload(rates) {
  if (!rates || typeof rates !== 'object') return 0;
  const candidates = [rates.final_rate, rates.base_rate, rates.rate, rates.amount, rates.ep];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

/**
 * Dashboard quick walk-in: guest step + review/payment, room & check-in date prefilled from calendar.
 */
const QuickBookingDrawer = ({ open, onClose, context, onSuccess, themeMode = 'light' }) => {
  const { can } = usePermission();
  const canManageAiCheckin = can(PERMISSIONS.AI_CHECKIN_MANAGE);
  const theme = useTheme();
  const isDark = themeMode === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const selectedCurrencySymbol = getBookingCurrencySymbol(getSelectedHotelCurrency());
  const quickBookFieldSurface = isDark ? drawerTheme.surface : '#ffffff';
  const drawerOverlays = useMemo(
    () => createBookingDrawerOverlayProps(isDark, quickBookFieldSurface),
    [isDark, quickBookFieldSurface]
  );
  const drawerSelectMenuProps = drawerOverlays.selectMenuProps;
  const quickBookOutlinedFieldSx = useMemo(
    () => getReservationOutlinedFieldSx(isDark, quickBookFieldSurface),
    [isDark, quickBookFieldSurface]
  );
  const accent = DASHBOARD_ACCENT;
  const {
    canSendPaymentLink,
    canSendPaymentLinkEmail,
    canSendPaymentLinkWhatsApp,
  } = useBookingPaymentLinkComm();

  const [step, setStep] = useState(0);
  const [loadingRoom, setLoadingRoom] = useState(false);
  const [roomLoadError, setRoomLoadError] = useState('');

  const [checkInDate, setCheckInDate] = useState(() => moment().startOf('day'));
  const [selectedDays, setSelectedDays] = useState(1);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [mealPlanCode, setMealPlanCode] = useState('');
  const [availableMealPlans, setAvailableMealPlans] = useState([]);
  /** Per-night rupees (editable); auto-filled from PMS when meal plan / date changes */
  const [nightRateAmount, setNightRateAmount] = useState('');
  const [pmsRatesSnapshot, setPmsRatesSnapshot] = useState(null);
  /** Calendar room + date range; pricing from meal plan / manual amount (no all-rooms inventory gate) */
  const [availabilityBundle, setAvailabilityBundle] = useState(null);

  const [guestTitle, setGuestTitle] = useState('');
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestMiddleName, setGuestMiddleName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestPhoneCountryCode, setGuestPhoneCountryCode] = useState('+91');
  const [guestAlternatePhone, setGuestAlternatePhone] = useState('');
  const [guestAlternatePhoneCountryCode, setGuestAlternatePhoneCountryCode] = useState('+91');
  const [guestAddress, setGuestAddress] = useState('');
  const [guestCity, setGuestCity] = useState('');
  const [guestState, setGuestState] = useState('');
  const [guestCountry, setGuestCountry] = useState('');
  const [guestPincode, setGuestPincode] = useState('');
  const [guestDOB, setGuestDOB] = useState('');
  const [guestGender, setGuestGender] = useState('');
  const [guestETA, setGuestETA] = useState('');
  const [guestNote, setGuestNote] = useState('');
  const [bookingReference, setBookingReference] = useState('');
  const [remarks, setRemarks] = useState('');
  const [uploadDocumentType, setUploadDocumentType] = useState('no_document');
  const [documentIdNumber, setDocumentIdNumber] = useState('');
  const [passportFrontId, setPassportFrontId] = useState('');
  const [passportBackId, setPassportBackId] = useState('');
  const [purposeOfStay, setPurposeOfStay] = useState('');
  const [purposeOfStayNote, setPurposeOfStayNote] = useState('');
  const [foreignGuestDetails, setForeignGuestDetails] = useState(EMPTY_FOREIGN_GUEST_DETAILS);
  const hotelCountry = useMemo(() => {
    const djangoContext = window.DJANGO_CONTEXT || window.djangoContext || {};
    return djangoContext.selectedHotel?.country || djangoContext.hotel?.country || '';
  }, [open]);

  const autoGstEnabled = isAutoGstEnabled();
  const [gstIncluded, setGstIncluded] = useState(false);
  const effectiveGstIncluded = autoGstEnabled || gstIncluded;
  const [taxInclusive, setTaxInclusive] = useState(false);
  const [allowDiscount, setAllowDiscount] = useState(false);
  const [discountMode, setDiscountMode] = useState('percentage');
  const [discountPercentage, setDiscountPercentage] = useState(5);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [allowExtraCharges, setAllowExtraCharges] = useState(false);
  const [extraChargesAmount, setExtraChargesAmount] = useState(0);
  const [extraChargesNote, setExtraChargesNote] = useState('');
  const [extraChargesItems, setExtraChargesItems] = useState([]);
  const [extraChargesBaseAmount, setExtraChargesBaseAmount] = useState(0);
  const [extraChargesGstAmount, setExtraChargesGstAmount] = useState(0);
  const [hasTaxRules, setHasTaxRules] = useState(false);
  const [gstPercentage, setGstPercentage] = useState(0);
  const [taxRules, setTaxRules] = useState([]);

  const [paymentType, setPaymentType] = useState('full');
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentDocument, setPaymentDocument] = useState('');
  const [collectionDate, setCollectionDate] = useState(() => moment());

  const [bookingType] = useState('nightly');
  const [isGuestDetailsValid, setIsGuestDetailsValid] = useState(false);
  const [selectedGuestId, setSelectedGuestId] = useState(null);
  const validateGuestDetailsRef = useRef(null);
  const submitIdempotencyKeyRef = useRef(null);

  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(EMPTY_COMPANY);
  const [companyDetailsSaved, setCompanyDetailsSaved] = useState(false);
  const [companyErrors, setCompanyErrors] = useState({});
  const [aiCheckinOpen, setAiCheckinOpen] = useState(false);
  const [guestProfileCompanySnapshot, setGuestProfileCompanySnapshot] = useState(null);
  const companyDetailsSavedRef = useRef(false);
  const companyAutofillRequestRef = useRef(0);

  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('Alert');
  const [alertSeverity, setAlertSeverity] = useState('info');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [successActionLoading, setSuccessActionLoading] = useState(null);
  const [successPaymentLinkBookingId, setSuccessPaymentLinkBookingId] = useState(0);
  const [successPaymentLinkAmount, setSuccessPaymentLinkAmount] = useState(0);
  const [showPaymentLinkOnSuccess, setShowPaymentLinkOnSuccess] = useState(false);
  const drawerRootRef = useRef(null);

  useEffect(() => {
    companyDetailsSavedRef.current = companyDetailsSaved;
  }, [companyDetailsSaved]);

  const successPaymentLinkBooking = useMemo(
    () => ({
      guest_email: guestEmail || '',
      guest_phone: `${String(guestPhoneCountryCode || '').replace(/\s/g, '')}${String(guestPhone || '').replace(/\D/g, '')}`,
      guest_name: [guestTitle, guestFirstName, guestMiddleName, guestLastName].filter(Boolean).join(' ').trim(),
    }),
    [
      guestEmail,
      guestPhone,
      guestPhoneCountryCode,
      guestTitle,
      guestFirstName,
      guestMiddleName,
      guestLastName,
    ]
  );

  const checkOutDate = useMemo(
    () => checkInDate.clone().add(selectedDays, 'days'),
    [checkInDate, selectedDays]
  );
  const nights = selectedDays;

  const showAlert = useCallback((message, title = 'Alert', severity = 'warning') => {
    setAlertMessage(message);
    setAlertTitle(title);
    setAlertSeverity(severity);
    setAlertDialogOpen(true);
  }, []);

  const scopedModalSx = useMemo(
    () => ({
      position: 'absolute',
      zIndex: theme.zIndex.modal,
    }),
    [theme.zIndex.modal]
  );

  const scopedBackdropSx = useMemo(
    () => ({
      position: 'absolute',
      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.72)' : 'rgba(0, 0, 0, 0.45)',
    }),
    [isDark]
  );

  const scopedPaperSx = useMemo(
    () => ({
      position: 'relative',
      margin: 2,
      maxHeight: 'calc(100% - 24px)',
      borderRadius: 2,
      backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
      border: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#000', 0.08)}`,
    }),
    [isDark]
  );

  /** Match WalkInForm success dialog styling; constrained for drawer-local modal */
  const successWalkInPaperSx = useMemo(
    () => ({
      borderRadius: 3,
      backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
      background: isDark
        ? drawerTheme.surfaceGradient
        : `linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)`,
      border: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
      boxShadow: isDark
        ? '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(76, 225, 182, 0.1), inset 0 1px 0 rgba(255,255,255,0.1)'
        : '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(76, 225, 182, 0.1), inset 0 1px 0 rgba(255,255,255,0.8)',
      position: 'relative',
      overflow: 'hidden',
      margin: 2,
      maxWidth: '100%',
      width: `min(100%, calc(100vw - 24px))`,
      maxHeight: 'calc(100% - 24px)',
      boxSizing: 'border-box',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: `linear-gradient(90deg, #4ce1b6 0%, #3ab896 50%, #4ce1b6 100%)`,
        boxShadow: '0 2px 8px rgba(76, 225, 182, 0.4)',
      },
    }),
    [isDark]
  );

  const successModalSx = useMemo(
    () => ({
      position: 'absolute',
      zIndex: theme.zIndex.modal + 2,
    }),
    [theme.zIndex.modal]
  );

  const drawerModalContainer = () => drawerRootRef.current ?? undefined;

  const resetGuestFields = useCallback(() => {
    setGuestTitle('');
    setGuestFirstName('');
    setGuestMiddleName('');
    setGuestLastName('');
    setGuestEmail('');
    setGuestPhone('');
    setGuestPhoneCountryCode('+91');
    setGuestAlternatePhone('');
    setGuestAlternatePhoneCountryCode('+91');
    setGuestAddress('');
    setGuestCity('');
    setGuestState('');
    setGuestPincode('');
    setGuestDOB('');
    setGuestGender('');
    setGuestETA('');
    setGuestNote('');
    setBookingReference('');
    setRemarks('');
    setUploadDocumentType('no_document');
    setDocumentIdNumber('');
    setPassportFrontId('');
    setPassportBackId('');
    setPurposeOfStay('');
    setPurposeOfStayNote('');
    setForeignGuestDetails(EMPTY_FOREIGN_GUEST_DETAILS);
    setCompanyDetails(EMPTY_COMPANY);
    setCompanyDetailsSaved(false);
    setCompanyErrors({});
    setGuestProfileCompanySnapshot(null);
  }, []);

  useEffect(() => {
    if (!open) {
      setStep(0);
      setRoomLoadError('');
      setSelectedRooms([]);
      setMealPlanCode('');
      setAvailableMealPlans([]);
      setNightRateAmount('');
      setPmsRatesSnapshot(null);
      setAvailabilityBundle(null);
      resetGuestFields();
      submitIdempotencyKeyRef.current = null;
      setSuccessDialogOpen(false);
      setBookingData(null);
      setSuccessActionLoading(null);
      setShowPaymentLinkOnSuccess(false);
      setSuccessPaymentLinkBookingId(0);
      setSuccessPaymentLinkAmount(0);
      return;
    }
    if (!context?.roomId) return;

    setCheckInDate(moment(context.checkInDate, 'YYYY-MM-DD', true).startOf('day'));
    setSelectedDays(1);
    setStep(0);
    resetGuestFields();
    submitIdempotencyKeyRef.current = null;

    const djangoContext = window.DJANGO_CONTEXT || {};
    const hotel = djangoContext.selectedHotel || null;
    if (hotel?.country) {
      const hc = String(hotel.country).trim();
      const countryObj = countriesData.countries.find(
        (c) => c.code === hc || c.name === hc || c.name?.toLowerCase() === hc.toLowerCase()
      );
      setGuestCountry(countryObj?.name || hc);
    }
  }, [open, context?.roomId, context?.checkInDate, resetGuestFields]);

  useEffect(() => {
    if (!open || !context?.roomId) return;
    getHotelRatePlans({
      params: {
        check_in_date: checkInDate.format('YYYY-MM-DD'),
        check_out_date: checkOutDate.format('YYYY-MM-DD'),
        room_type_id: context.roomTypeId,
      },
    })
      .then((data) => {
        const ratePlans = data?.rate_plans || data?.ratePlans || [];
        const opts = Array.isArray(ratePlans)
          ? ratePlans.map((rp) => ({
              value: String(rp.code || '')
                .trim()
                .toUpperCase(),
              label: rp.name || rp.code || '',
            }))
          : [];
        setAvailableMealPlans(opts);
        setMealPlanCode((prev) => {
          const u = String(prev || '')
            .trim()
            .toUpperCase();
          if (u && opts.some((o) => o.value === u)) return u;
          const ep = opts.find((o) => o.value === 'EP');
          if (ep) return ep.value;
          return opts[0]?.value || '';
        });
      })
      .catch(() => {
        setAvailableMealPlans([]);
        setMealPlanCode('');
      });
  }, [open, context?.roomId, context?.roomTypeId, checkInDate, checkOutDate]);

  useEffect(() => {
    if (!open || !context?.roomTypeId || !mealPlanCode || availableMealPlans.length === 0) return;
    const planCode = String(mealPlanCode).trim().toUpperCase();
    if (!availableMealPlans.some((o) => o.value === planCode)) return;
    let cancelled = false;
    (async () => {
      try {
        const rd = await getRatesByRatePlan(
          context.roomTypeId,
          checkInDate.format('YYYY-MM-DD'),
          planCode,
          checkOutDate.format('YYYY-MM-DD')
        );
        if (cancelled) return;
        const rates = rd?.rates && typeof rd.rates === 'object' ? rd.rates : null;
        setPmsRatesSnapshot(rates);
        const n = nightlyRateFromRatesPayload(rates);
        setNightRateAmount(n > 0 ? String(Math.round(n)) : '0');
      } catch {
        if (!cancelled) {
          setPmsRatesSnapshot(null);
          setNightRateAmount('0');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, context?.roomTypeId, mealPlanCode, checkInDate, checkOutDate, availableMealPlans]);

  useEffect(() => {
    if (!open || !context?.roomId) {
      setAvailabilityBundle(null);
      setRoomLoadError('');
      setLoadingRoom(false);
      return;
    }

    const ci = checkInDate.clone().startOf('day');
    const co = ci.clone().add(selectedDays, 'days');
    if (!co.isAfter(ci)) {
      setAvailabilityBundle(null);
      setRoomLoadError('Check-out must be after check-in.');
      setLoadingRoom(false);
      return;
    }

    setLoadingRoom(false);
    setRoomLoadError('');
    const ciStr = ci.format('YYYY-MM-DD');
    const coStr = co.format('YYYY-MM-DD');
    const physical = {
      id: context.roomId,
      room_number: context.roomNumber || '',
      status: 'available',
      clean_status: null,
      floor: null,
      is_available: true,
      is_checkout: false,
      room_type_has_blocks: false,
    };
    const rtMeta = {
      id: context.roomTypeId,
      name: context.roomTypeName || '',
      base_adults: 2,
      base_children: 0,
      max_guests: 4,
      extra_adult_charge: 0,
      extra_child_charge: 0,
    };
    setAvailabilityBundle({ physical, rtMeta, ciStr, coStr });
  }, [
    open,
    context?.roomId,
    context?.roomNumber,
    context?.roomTypeId,
    context?.roomTypeName,
    checkInDate,
    selectedDays,
  ]);

  useEffect(() => {
    if (!open || !context?.roomId) return;
    if (!availabilityBundle?.physical) {
      setSelectedRooms([]);
      return;
    }
    const { physical, rtMeta, ciStr, coStr } = availabilityBundle;
    const ratePerNight = Math.max(
      0,
      Math.round(Number(String(nightRateAmount).replace(/,/g, '').trim()) || 0)
    );
    const { baseAdults, baseChildren } = resolveRoomTypeLimits(rtMeta || {});
    const adults = Math.max(baseAdults, 1);
    const children = 0;
    const extraAdultsCount = Math.max(0, adults - baseAdults);
    const extraChildrenCount = Math.max(0, children - baseChildren);
    const effectiveExtraAdultRate =
      Number(pmsRatesSnapshot?.extra_adult_rate ?? rtMeta?.extra_adult_charge ?? 0) || 0;
    const effectiveExtraChildRate =
      Number(pmsRatesSnapshot?.extra_child_rate ?? rtMeta?.extra_child_charge ?? 0) || 0;
    const effectiveDays = selectedDays;
    const basePrice = ratePerNight * effectiveDays;
    const totalExtraAdultCharge = effectiveExtraAdultRate * extraAdultsCount * effectiveDays;
    const totalExtraChildCharge = effectiveExtraChildRate * extraChildrenCount * effectiveDays;
    const prices = {
      basePrice,
      extraAdultCharge: totalExtraAdultCharge,
      extraChildCharge: totalExtraChildCharge,
      discount: 0,
      priceAfterDiscount: basePrice + totalExtraAdultCharge + totalExtraChildCharge,
      total: basePrice + totalExtraAdultCharge + totalExtraChildCharge,
    };
    const row = {
      roomType: rtMeta?.name || context.roomTypeName || '',
      roomTypeId: context.roomTypeId,
      roomId: context.roomId,
      roomNumber: context.roomNumber || physical.room_number,
      mealPlan: mealPlanCode || 'EP',
      rooms: 1,
      days: effectiveDays,
      adults,
      children,
      ratePerNight,
      extraAdultRate: effectiveExtraAdultRate,
      extraChildRate: effectiveExtraChildRate,
      extraAdultsCount,
      extraChildrenCount,
      baseAdults,
      baseChildren,
      checkInDate: ciStr,
      checkOutDate: coStr,
      ...prices,
    };
    setSelectedRooms([row]);
  }, [
    open,
    context,
    availabilityBundle,
    mealPlanCode,
    nightRateAmount,
    pmsRatesSnapshot,
    selectedDays,
  ]);

  useEffect(() => {
    getTaxRulesAvailability('INR')
      .then((data) => {
        setHasTaxRules(data.has_tax_rules || false);
        setTaxRules(Array.isArray(data.tax_rules) ? data.tax_rules : []);
        if (data.tax_percentage != null) setGstPercentage(data.tax_percentage);
      })
      .catch(() => {
        setHasTaxRules(false);
        setGstPercentage(0);
        setTaxRules([]);
      });
  }, []);

  useEffect(() => {
    const phoneDigits = (guestPhone || '').replace(/\D/g, '');
    if (!isValidPhoneForCountry(phoneDigits, guestPhoneCountryCode)) {
      companyAutofillRequestRef.current += 1;
      if (!companyDetailsSavedRef.current) {
        setCompanyDetails({ ...EMPTY_COMPANY });
        setCompanyDetailsSaved(false);
        setCompanyErrors({});
      }
      setGuestProfileCompanySnapshot(null);
      return undefined;
    }
    const requestId = ++companyAutofillRequestRef.current;
    const timer = setTimeout(async () => {
      try {
        const searchResult = await searchGuestByPhone(phoneDigits);
        if (companyAutofillRequestRef.current !== requestId) return;
        const guestId = Number(searchResult?.guest?.id);
        if (!Number.isInteger(guestId) || guestId <= 0) {
          setGuestProfileCompanySnapshot(null);
          return;
        }
        const customerDetail = await getCustomerDetail(guestId, 1, 1);
        if (companyAutofillRequestRef.current !== requestId) return;
        const customer = customerDetail?.customer || {};
        const normalizedTaxType = String(customer.company_tax_id_type || '').trim().toLowerCase();
        const taxIdType = ['gstin', 'pan', 'tan', 'cin', 'other'].includes(normalizedTaxType)
          ? normalizedTaxType
          : 'gstin';
        const nextCompanyDetails = {
          taxIdType,
          taxIdNumber: String(customer.company_tax_id_number || '').trim(),
          companyName: String(customer.company_name || '').trim(),
          companyPhone: String(customer.company_phone || '').trim(),
          companyEmail: String(customer.company_email || '').trim(),
          companyAddress: String(customer.company_address || '').trim(),
          companyCity: String(customer.company_city || '').trim(),
          companyState: String(customer.company_state || '').trim(),
          companyCountry: String(customer.company_country || '').trim(),
          companyPincode: String(customer.company_pincode || customer.company_postal_code || '').trim(),
        };
        const hasAnyCompanyValue = hasAnyCompanyFieldValues(nextCompanyDetails);
        setGuestProfileCompanySnapshot(hasAnyCompanyValue ? nextCompanyDetails : null);
        if (!companyDetailsSavedRef.current) {
          setCompanyDetails({ ...EMPTY_COMPANY });
          setCompanyDetailsSaved(false);
          setCompanyErrors({});
        }
      } catch (_) {
        /* ignore */
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [guestPhone, guestPhoneCountryCode]);

  const handleGuestPhoneCountryCodeChange = useCallback((newCode) => {
    if (VALID_DIAL_CODES.has(newCode)) setGuestPhoneCountryCode(newCode);
  }, []);
  const handleGuestAlternatePhoneCountryCodeChange = useCallback((newCode) => {
    if (VALID_DIAL_CODES.has(newCode)) setGuestAlternatePhoneCountryCode(newCode);
  }, []);

  const priceBreakdown = useMemo(() => {
    let totalBasePrice = 0;
    let totalExtraAdultCharge = 0;
    let totalExtraChildCharge = 0;
    let totalRoomBasePrice = 0;
    const currentNights = nights || 1;
    selectedRooms.forEach((room) => {
      const ratePerNight = room.ratePerNight || 0;
      const extraAdultRate = room.extraAdultRate || 0;
      const extraChildRate = room.extraChildRate || 0;
      const extraAdultsCount = room.extraAdultsCount || 0;
      const extraChildrenCount = room.extraChildrenCount || 0;
      const basePrice = ratePerNight * currentNights;
      const extraAdultCharge = extraAdultRate * extraAdultsCount * currentNights;
      const extraChildCharge = extraChildRate * extraChildrenCount * currentNights;
      totalExtraAdultCharge += extraAdultCharge;
      totalExtraChildCharge += extraChildCharge;
      totalRoomBasePrice += basePrice;
      totalBasePrice += basePrice + extraAdultCharge + extraChildCharge;
    });
    const roomLines = selectedRooms.map((room) => {
      const ratePerNight = room.ratePerNight || 0;
      const extraAdultRate = room.extraAdultRate || 0;
      const extraChildRate = room.extraChildRate || 0;
      const extraAdultsCount = room.extraAdultsCount || 0;
      const extraChildrenCount = room.extraChildrenCount || 0;
      return {
        ratePerNight: ratePerNight + (extraAdultRate * extraAdultsCount) + (extraChildRate * extraChildrenCount),
        amount: (ratePerNight * currentNights)
          + (extraAdultRate * extraAdultsCount * currentNights)
          + (extraChildRate * extraChildrenCount * currentNights),
      };
    });
    return calculateBookingPriceBreakdown({
      roomLines,
      taxRules,
      gstIncluded: effectiveGstIncluded,
      taxInclusive,
      allowDiscount,
      discountMode,
      discountPercentage,
      discountAmount,
      allowExtraCharges,
      extraChargesAmount,
      extraChargesNote,
      totalBasePrice,
      totalRoomBasePrice,
      totalExtraAdultCharge,
      totalExtraChildCharge,
      gstPercentage,
      hasTaxRules,
    });
  }, [
    selectedRooms,
    nights,
    allowDiscount,
    discountMode,
    effectiveGstIncluded,
    taxInclusive,
    discountPercentage,
    discountAmount,
    allowExtraCharges,
    extraChargesAmount,
    extraChargesNote,
    gstPercentage,
    hasTaxRules,
    taxRules,
  ]);

  const hasCompanyDetails =
    companyDetailsSaved && Boolean(companyDetails.companyName?.trim());

  const validateCompanyDetails = () => {
    const nextErrors = {};
    if (!companyDetails.companyName.trim()) {
      nextErrors.companyName = 'Company name is required';
    }
    if (companyDetails.companyEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(companyDetails.companyEmail.trim())) {
        nextErrors.companyEmail = 'Enter a valid company email';
      }
    }
    setCompanyErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCompanySave = () => {
    if (!validateCompanyDetails()) return;
    setCompanyDetailsSaved(true);
    setCompanyDialogOpen(false);
  };

  const handleOpenCompanyDialog = () => {
    setCompanyErrors({});
    setCompanyDetails((prev) => {
      if (companyDetailsSaved) return prev;
      if (!isCompanyFormEffectivelyEmpty(prev)) return prev;
      if (guestProfileCompanySnapshot) return { ...guestProfileCompanySnapshot };
      return prev;
    });
    setCompanyDialogOpen(true);
  };

  const handleRemoveCompanyDetailsForBooking = () => {
    setCompanyDetails({ ...EMPTY_COMPANY });
    setCompanyDetailsSaved(false);
    setCompanyErrors({});
    setCompanyDialogOpen(false);
  };

  const applyAiGuestFields = useCallback((guest, previews) => {
    const g = guest || {};
    const p = typeof previews === 'string' ? { frontDataUrl: previews, backDataUrl: null } : previews || {};
    const applyTrimmed = (val, setter) => {
      if (val == null || val === '') return;
      const t = String(val).trim();
      if (t !== '') setter(t);
    };
    applyTrimmed(g.guest_title, setGuestTitle);
    applyTrimmed(g.guest_first_name, setGuestFirstName);
    applyTrimmed(g.guest_middle_name, setGuestMiddleName);
    applyTrimmed(g.guest_last_name, setGuestLastName);
    applyTrimmed(g.guest_email, setGuestEmail);
    applyTrimmed(g.guest_address, setGuestAddress);
    applyTrimmed(g.guest_city, setGuestCity);
    applyTrimmed(g.guest_state, setGuestState);
    const countryCombined = String(g.guest_country || '').trim() || String(g.nationality || '').trim();
    if (countryCombined) setGuestCountry(countryCombined);
    applyTrimmed(g.guest_pincode, setGuestPincode);
    applyTrimmed(g.guest_dob, setGuestDOB);
    applyTrimmed(g.guest_gender, setGuestGender);
    const docType = g.upload_document_type || 'aadhaar';
    setUploadDocumentType(docType);
    if (g.document_id_number != null && String(g.document_id_number).trim() !== '') {
      setDocumentIdNumber(normalizeDocumentIdInput(docType, g.document_id_number));
    }
    if (p.frontDataUrl) setPassportFrontId(p.frontDataUrl);
    if (p.backDataUrl) setPassportBackId(p.backDataUrl);
  }, []);

  const handleNext = () => {
    if (step === 0) {
      if (loadingRoom || selectedRooms.length === 0) {
        showAlert('Room is still loading or unavailable.', 'Quick booking', 'warning');
        return;
      }
      const nightly = Math.round(Number(selectedRooms[0]?.ratePerNight) || 0);
      if (nightly < 0) {
        showAlert('Nightly amount cannot be negative.', 'Quick booking', 'warning');
        return;
      }
      if (validateGuestDetailsRef.current && typeof validateGuestDetailsRef.current === 'function') {
        const ok = validateGuestDetailsRef.current();
        if (ok) {
          submitIdempotencyKeyRef.current = null;
          setStep(1);
        }
      } else if (isGuestDetailsValid) {
        submitIdempotencyKeyRef.current = null;
        setStep(1);
      }
    }
  };

  const handleBack = () => {
    if (step === 1) submitIdempotencyKeyRef.current = null;
    setStep((s) => Math.max(0, s - 1));
  };

  const extractQuickBookActionError = (error, fallbackMessage) =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallbackMessage;

  const getQuickBookActionBookingIds = useCallback(() => {
    if (!bookingData) return [];
    const bookingIds = [];
    if (Array.isArray(bookingData.bookings) && bookingData.bookings.length > 0) {
      bookingData.bookings.forEach((booking) => {
        const idCandidate = booking?.booking_id ?? booking?.id ?? null;
        const parsedId = Number(idCandidate);
        if (Number.isInteger(parsedId) && parsedId > 0) {
          bookingIds.push(parsedId);
        }
      });
    }
    if (bookingIds.length > 0) {
      return [...new Set(bookingIds)];
    }
    const fallbackId = Number(bookingData.booking_id ?? bookingData.id ?? null);
    if (Number.isInteger(fallbackId) && fallbackId > 0) {
      return [fallbackId];
    }
    return [];
  }, [bookingData]);

  const handleCloseSuccessDialog = useCallback(() => {
    submitIdempotencyKeyRef.current = null;
    setSuccessDialogOpen(false);
    setSuccessActionLoading(null);
    setBookingData(null);
    setShowPaymentLinkOnSuccess(false);
    setSuccessPaymentLinkBookingId(0);
    setSuccessPaymentLinkAmount(0);
    onSuccess?.();
    onClose();
  }, [onSuccess, onClose]);

  const handleDownloadInvoice = useCallback(async () => {
    const bookingIds = getQuickBookActionBookingIds();
    if (!bookingIds.length) {
      showAlert('Booking details not found for invoice download', 'Warning', 'warning');
      return;
    }
    const groupCode =
      bookingData?.group_booking?.group_booking_code
      || bookingData?.group_booking?.code
      || bookingData?.group_booking_code
      || null;
    // Group stays: one combined GRP invoice from confirm popup only.
    const invoiceIds = groupCode && bookingIds.length > 1 ? [bookingIds[0]] : bookingIds;
    setSuccessActionLoading('invoice');
    try {
      for (const bookingId of invoiceIds) {
        await downloadBookingInvoice(bookingId, 'hotel', {
          groupInvoice: Boolean(groupCode),
        });
      }
      showAlert(
        groupCode
          ? `Group invoice downloaded (${groupCode})`
          : invoiceIds.length > 1
            ? `Invoices downloaded for ${invoiceIds.length} bookings`
            : 'Invoice downloaded successfully',
        'Success',
        'success'
      );
    } catch (error) {
      showAlert(extractQuickBookActionError(error, 'Invoice download failed'), 'Error', 'error');
    } finally {
      setSuccessActionLoading(null);
    }
  }, [bookingData, getQuickBookActionBookingIds, showAlert]);

  const handleDownloadVoucher = useCallback(async () => {
    const bookingIds = getQuickBookActionBookingIds();
    if (!bookingIds.length) {
      showAlert('Booking details not found for voucher download', 'Warning', 'warning');
      return;
    }
    const groupCode =
      bookingData?.group_booking?.group_booking_code
      || bookingData?.group_booking?.code
      || bookingData?.group_booking_code
      || null;
    const voucherIds = groupCode && bookingIds.length > 1 ? [bookingIds[0]] : bookingIds;
    setSuccessActionLoading('voucher');
    try {
      for (const bookingId of voucherIds) {
        await downloadBookingConfirmation(bookingId, {
          groupVoucher: Boolean(groupCode),
        });
      }
      showAlert(
        groupCode
          ? `Group voucher downloaded (${groupCode})`
          : voucherIds.length > 1
            ? `Vouchers downloaded for ${voucherIds.length} bookings`
            : 'Voucher downloaded successfully',
        'Success',
        'success'
      );
    } catch (error) {
      showAlert(extractQuickBookActionError(error, 'Voucher download failed'), 'Error', 'error');
    } finally {
      setSuccessActionLoading(null);
    }
  }, [bookingData, getQuickBookActionBookingIds, showAlert]);

  const handleSendWhatsApp = useCallback(async () => {
    const bookingIds = getQuickBookActionBookingIds();
    const phoneNumber = guestPhone?.replace(/\D/g, '');
    const groupCode =
      bookingData?.group_booking?.group_booking_code
      || bookingData?.group_booking?.code
      || bookingData?.group_booking_code
      || null;
    const actionIds = groupCode && bookingIds.length > 1 ? [bookingIds[0]] : bookingIds;
    const bookingCode =
      groupCode
      || bookingData?.bookings?.[0]?.booking_code
      || bookingData?.booking_code
      || bookingData?.code;
    if (!actionIds.length) {
      showAlert('Booking details not found for sending WhatsApp', 'Warning', 'warning');
      return;
    }
    if (!phoneNumber) {
      showAlert('Phone number is required to send WhatsApp message', 'Warning', 'warning');
      return;
    }
    setSuccessActionLoading('whatsapp');
    try {
      let sentCount = 0;
      const errors = [];
      let lastRes = null;
      for (const bookingId of actionIds) {
        const res = await sendBookingConfirmation(bookingId, {
          send_email: false,
          send_whatsapp: true,
          groupInvoice: Boolean(groupCode),
          groupVoucher: Boolean(groupCode),
        });
        lastRes = res;
        if (res?.whatsapp_sent) {
          sentCount += 1;
        } else if (Array.isArray(res?.errors) && res.errors.length > 0) {
          errors.push(...res.errors);
        }
      }
      if (sentCount > 0) {
        const sentDocs =
          Array.isArray(lastRes?.whatsapp_sent_documents) && lastRes.whatsapp_sent_documents.length
            ? lastRes.whatsapp_sent_documents.join(' and ')
            : groupCode
              ? 'group voucher and invoice'
              : 'voucher and invoice';
        showAlert(
          formatWhatsAppSuccessMessage(lastRes, {
            prefix: groupCode
              ? `${sentDocs.charAt(0).toUpperCase()}${sentDocs.slice(1)} sent on WhatsApp (${groupCode})`
              : `${sentDocs.charAt(0).toUpperCase()}${sentDocs.slice(1)} sent on WhatsApp`,
            bookingCode,
          }),
          'Success',
          'success'
        );
      } else {
        showAlert(errors[0] || 'WhatsApp not sent', 'Warning', 'warning');
      }
    } catch (error) {
      showAlert(extractQuickBookActionError(error, 'WhatsApp send failed'), 'Error', 'error');
    } finally {
      setSuccessActionLoading(null);
    }
  }, [bookingData, getQuickBookActionBookingIds, guestPhone, showAlert]);

  const handleSendEmail = useCallback(async () => {
    const bookingIds = getQuickBookActionBookingIds();
    const email = guestEmail;
    const groupCode =
      bookingData?.group_booking?.group_booking_code
      || bookingData?.group_booking?.code
      || bookingData?.group_booking_code
      || null;
    const invoiceIds = groupCode && bookingIds.length > 1 ? [bookingIds[0]] : bookingIds;
    if (!invoiceIds.length) {
      showAlert('Booking details not found for sending email', 'Warning', 'warning');
      return;
    }
    if (!email) {
      showAlert('Email address is required to send email', 'Warning', 'warning');
      return;
    }
    setSuccessActionLoading('email');
    try {
      let sentCount = 0;
      const errors = [];
      for (const bookingId of invoiceIds) {
        const res = await sendBookingConfirmation(bookingId, {
          send_email: true,
          send_whatsapp: false,
          groupInvoice: Boolean(groupCode),
          groupVoucher: Boolean(groupCode),
        });
        if (res?.email_sent) {
          sentCount += 1;
        } else if (Array.isArray(res?.errors) && res.errors.length > 0) {
          errors.push(...res.errors);
        }
      }
      if (sentCount > 0) {
        const successMessage = groupCode
          ? `Group voucher and invoice sent (${groupCode})`
          : invoiceIds.length > 1
            ? `Confirmation and invoice sent by email for ${sentCount}/${invoiceIds.length} bookings`
            : 'Confirmation and invoice sent to guest email';
        showAlert(successMessage, 'Success', 'success');
      } else {
        showAlert(errors[0] || 'Email not sent', 'Warning', 'warning');
      }
    } catch (error) {
      showAlert(extractQuickBookActionError(error, 'Email send failed'), 'Error', 'error');
    } finally {
      setSuccessActionLoading(null);
    }
  }, [bookingData, getQuickBookActionBookingIds, guestEmail, showAlert]);

  const walkInSuccessGuestLine = `${guestFirstName} ${guestLastName || ''}`.trim() || '—';
  const successGroupBookingCode =
    bookingData?.group_booking?.group_booking_code
    || bookingData?.group_booking?.code
    || bookingData?.group_booking_code
    || null;

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if ((paymentType === 'full' || paymentType === 'partial') && !paymentMethod) {
      showAlert('Please select a payment method.', 'Payment', 'warning');
      return;
    }
    if (!selectedRooms.length) return;

    setIsSubmitting(true);
    setSuccessDialogOpen(true);
    setBookingData({ processing: true });
    try {
      const currentNights = nights || 1;
      const allRoomsData = selectedRooms.map((room) => {
        const ratePerNight = room.ratePerNight || 0;
        const extraAdultRate = room.extraAdultRate || 0;
        const extraChildRate = room.extraChildRate || 0;
        const extraAdultsCount = room.extraAdultsCount || 0;
        const extraChildrenCount = room.extraChildrenCount || 0;
        const basePrice = ratePerNight * currentNights;
        const extraAdultCharge = extraAdultRate * extraAdultsCount * currentNights;
        const extraChildCharge = extraChildRate * extraChildrenCount * currentNights;
        return {
          roomTypeId: room.roomTypeId,
          roomId: room.roomId,
          roomNumber: room.roomNumber,
          mealPlan: room.mealPlan || 'EP',
          rooms: 1,
          adults: room.adults,
          children: room.children,
          basePrice,
          extraAdultCharge,
          extraChildCharge,
          discount: room.discount || 0,
        };
      });

      const normalizedUploadDocumentType =
        uploadDocumentType === 'no_document' ? '' : uploadDocumentType || '';
      const guestData = {
        ...(selectedGuestId ? { id: selectedGuestId } : {}),
        title: guestTitle || '',
        first_name: guestFirstName.trim(),
        middle_name: guestMiddleName.trim() || '',
        last_name: guestLastName.trim(),
        email: guestEmail.trim() || '',
        phone: guestPhone.trim().replace(/\D/g, ''),
        country_code: guestPhoneCountryCode || '+91',
        alternate_phone: guestAlternatePhone.trim().replace(/\D/g, '') || '',
        alternate_phone_country_code: guestAlternatePhoneCountryCode || '+91',
        address_line1: guestAddress.trim() || '',
        city: guestCity.trim() || '',
        state: guestState.trim() || '',
        country: (() => {
          if (!guestCountry) return '';
          const countryObj = countriesData.countries.find(
            (c) => c.name === guestCountry || c.code === guestCountry
          );
          return countryObj ? countryObj.code : guestCountry.trim();
        })(),
        pincode: guestPincode.trim() || '',
        dob: guestDOB || '',
        gender: guestGender || '',
        purpose_of_stay: purposeOfStay || '',
        purpose_of_stay_note: purposeOfStay === 'other' ? (purposeOfStayNote || '').trim() : '',
        eta: guestETA || '',
        guest_note: guestNote || '',
        booking_reference: bookingReference || '',
        booking_source: hasCompanyDetails ? 'corporate' : 'walk_in',
        remarks: remarks || '',
        company_name: hasCompanyDetails ? companyDetails.companyName || '' : '',
        company_tax_id_type: hasCompanyDetails ? companyDetails.taxIdType || '' : '',
        company_tax_id_number: hasCompanyDetails ? companyDetails.taxIdNumber || '' : '',
        company_phone: hasCompanyDetails ? companyDetails.companyPhone || '' : '',
        company_email: hasCompanyDetails ? companyDetails.companyEmail || '' : '',
        company_address: hasCompanyDetails ? companyDetails.companyAddress || '' : '',
        company_city: hasCompanyDetails ? companyDetails.companyCity || '' : '',
        company_state: hasCompanyDetails ? companyDetails.companyState || '' : '',
        company_country: hasCompanyDetails ? companyDetails.companyCountry || '' : '',
        company_pincode: hasCompanyDetails ? companyDetails.companyPincode || '' : '',
        company_postal_code: hasCompanyDetails ? companyDetails.companyPincode || '' : '',
        document_type: normalizedUploadDocumentType,
        id_number:
          normalizeDocumentIdForSubmit(normalizedUploadDocumentType, documentIdNumber) || '',
        id_front:
          passportFrontId &&
          typeof passportFrontId === 'string' &&
          (passportFrontId.startsWith('data:image') ||
            passportFrontId.startsWith('http://') ||
            passportFrontId.startsWith('https://'))
            ? passportFrontId
            : '',
        id_back:
          passportBackId &&
          typeof passportBackId === 'string' &&
          (passportBackId.startsWith('data:image') ||
            passportBackId.startsWith('http://') ||
            passportBackId.startsWith('https://'))
            ? passportBackId
            : '',
        ...(isForeignGuestCountry(guestCountry, hotelCountry)
          ? foreignDetailsToGuestApiPayload(foreignGuestDetails)
          : {}),
      };

      if (!submitIdempotencyKeyRef.current) {
        submitIdempotencyKeyRef.current = generateIdempotencyKey();
      }
      const formData = {
        bookingType,
        checkInDate: checkInDate.format('YYYY-MM-DD'),
        checkOutDate: checkOutDate.format('YYYY-MM-DD'),
        rooms: allRoomsData,
        guest: guestData,
        gstIncluded: effectiveGstIncluded,
        taxInclusive,
        allowDiscount,
        discountPercentage: allowDiscount && discountMode === 'percentage' ? discountPercentage : 0,
        discountAmount: allowDiscount && discountMode === 'amount' ? discountAmount : 0,
        allowExtraCharges,
        extraChargesAmount: allowExtraCharges ? extraChargesAmount : 0,
        extraChargesNote: allowExtraCharges ? extraChargesNote.trim() : '',
        paymentType,
        // Full payment must match inclusive/exclusive invoice total (avoid stale exclusive amount).
        paymentAmount:
          paymentType === 'full'
            ? toCurrencyAmount(priceBreakdown?.total)
            : paymentAmount
              ? toCurrencyAmount(paymentAmount)
              : 0,
        paymentMethod: paymentMethod || '',
        referenceNumber: referenceNumber || '',
        paymentDocument: paymentDocument || '',
        collectionDate: toPaymentProcessedAtIso(collectionDate) || '',
        idempotencyKey: submitIdempotencyKeyRef.current,
        /** Quick booking: auto check-in only when check-in is today; future stays confirmed for Check-in drawer */
        autoCheckIn: checkInDate.clone().startOf('day').isSame(moment().startOf('day'), 'day'),
        /** PMS daily inventory / slot checks skipped; server only blocks overlapping nights on this room */
        quickBooking: true,
      };

      const response = await submitWalkIn(formData);
      const allBookings = response.bookings || [];
      const combinedResponse = {
        success: response.success !== false,
        message: response.message || `Successfully created ${allBookings.length} booking(s)`,
        bookings: allBookings,
        total_bookings: response.total_bookings || allBookings.length,
        booking_code: response.booking_code,
        booking_id: response.booking_id,
        id: response.id,
        code: response.code,
      };
      setBookingData(combinedResponse);
      const bidList = [];
      (allBookings || []).forEach((booking) => {
        const idCandidate = booking?.booking_id ?? booking?.id ?? null;
        const parsedId = Number(idCandidate);
        if (Number.isInteger(parsedId) && parsedId > 0) bidList.push(parsedId);
      });
      const uniqIds = [...new Set(bidList)];
      const firstPlId = uniqIds.length
        ? uniqIds[0]
        : Number(response.booking_id ?? response.id ?? 0);
      const plId = Number.isInteger(firstPlId) && firstPlId > 0 ? firstPlId : 0;
      const dueForLink = computeAmountDueForPaymentLink({
        total: priceBreakdown?.total,
        paymentMethod,
        paymentType,
        paymentAmount,
        isWalkIn: true,
      });
      setSuccessPaymentLinkBookingId(plId);
      setSuccessPaymentLinkAmount(dueForLink);
      setShowPaymentLinkOnSuccess(plId > 0 && dueForLink > 0 && canSendPaymentLink);
    } catch (error) {
      setSuccessDialogOpen(false);
      setBookingData(null);
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Booking failed.';
      showAlert(msg, 'Error', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const companyCountries = Array.isArray(countriesData?.countries) ? countriesData.countries : [];
  const selectedCompanyCountryObj =
    companyCountries.find(
      (country) =>
        country?.name === companyDetails.companyCountry ||
        country?.code === companyDetails.companyCountry ||
        country?.name?.toLowerCase() === (companyDetails.companyCountry || '').toLowerCase()
    ) || null;
  const companyStates = selectedCompanyCountryObj?.states || [];
  const selectedCompanyStateObj =
    companyStates.find((state) => state?.name === companyDetails.companyState) || null;
  const companyCities = selectedCompanyStateObj?.cities || [];

  /** Same gradient + chrome as main quick-book drawer (company drawer matches). */
  const quickBookingDrawerSurfaceSx = {
    background: isDark
      ? `linear-gradient(165deg, ${alpha('#1e1e24', 0.98)} 0%, #232329 45%, ${alpha('#1a1a20', 0.99)} 100%)`
      : `linear-gradient(165deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%)`,
    borderLeft: isDark ? `1px solid ${alpha('#fff', 0.06)}` : `1px solid ${alpha('#000', 0.06)}`,
    boxShadow: isDark ? '-12px 0 48px rgba(0,0,0,0.55)' : '-8px 0 40px rgba(0,0,0,0.08)',
  };

  const drawerPaperSx = {
    width: QUICK_BOOKING_DRAWER_WIDTH_SX,
    maxWidth: '100vw',
    ...quickBookingDrawerSurfaceSx,
  };

  const companyDrawerPaperSx = {
    ...drawerPaperSx,
    zIndex: theme.zIndex.drawer + 2,
  };

  const companyFieldSx = {
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.05),
      '&:hover': { bgcolor: isDark ? alpha('#fff', 0.09) : alpha('#000', 0.07) },
      '&.Mui-focused': { bgcolor: isDark ? alpha('#fff', 0.11) : alpha('#000', 0.07) },
      '&.Mui-disabled': { bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.04) },
      '&.Mui-error': {
        bgcolor: isDark ? alpha(theme.palette.error.main, 0.14) : alpha(theme.palette.error.main, 0.08),
      },
    },
    '& .MuiInputLabel-root': {
      color: isDark ? alpha('#fff', 0.62) : alpha('#000', 0.62),
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: isDark ? accent : theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-input': {
      color: isDark ? alpha('#fff', 0.94) : alpha('#000', 0.87),
    },
    '& .MuiFormHelperText-root': {
      color: isDark ? alpha('#fff', 0.48) : undefined,
      mx: 0,
    },
    '& .MuiAutocomplete-popupIndicator .MuiSvgIcon-root, & .MuiAutocomplete-clearIndicator .MuiSvgIcon-root':
      {
        color: isDark ? alpha('#fff', 0.55) : alpha('#000', 0.45),
      },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Drawer anchor="right" open={open && !!context} onClose={onClose} sx={getDrawerRootSx()} PaperProps={{ sx: drawerPaperSx }}>
        <Box ref={drawerRootRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          {/* Top: centered stepper (reference layout); close top-right */}
          <Box
            sx={{
              position: 'relative',
              px: 2,
              pt: 2.5,
              pb: 2,
              borderBottom: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#000', 0.08)}`,
              background: isDark ? alpha('#000', 0.2) : alpha('#fff', 0.5),
            }}
          >
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                border: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#000', 0.12)}`,
                bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.03),
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            <Box
              sx={{
                textAlign: 'center',
                px: { xs: 4, sm: 6 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  rowGap: 1,
                }}
              >
                <Typography
                  variant="h6"
                  component="span"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: isDark ? '#fff' : '#0f172a',
                  }}
                >
                  Quick booking
                </Typography>
                {context && (
                  <>
                    <Chip
                      label={String(context.roomNumber || context.roomId || 'Room')}
                      size="small"
                      sx={{
                        fontWeight: 800,
                        height: 28,
                        backgroundColor: alpha(accent, isDark ? 0.22 : 0.2),
                        color: isDark ? accent : '#0d3d32',
                        border: `1px solid ${alpha(accent, 0.45)}`,
                      }}
                    />
                    <Typography
                      component="span"
                      sx={{
                        color: isDark ? alpha('#fff', 0.35) : alpha('#000', 0.35),
                        fontWeight: 700,
                        userSelect: 'none',
                      }}
                    >
                      ·
                    </Typography>
                    <Chip
                      label={context.roomTypeName || 'Room type'}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        height: 28,
                        maxWidth: { xs: 200, sm: 280 },
                        backgroundColor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06),
                        border: `1px solid ${isDark ? alpha('#fff', 0.14) : alpha('#000', 0.12)}`,
                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                      }}
                    />
                  </>
                )}
              </Box>

              {/* Horizontal stepper: 1 —— 2 */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0,
                  mt: 2.5,
                }}
              >
                {[0, 1].map((i) => {
                  const active = step === i;
                  const done = step > i;
                  const inactiveBd = isDark ? alpha('#fff', 0.18) : alpha('#000', 0.14);
                  const inactiveFg = isDark ? alpha('#fff', 0.45) : alpha('#000', 0.45);
                  return (
                    <React.Fragment key={i}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                          fontSize: '1rem',
                          flexShrink: 0,
                          border: done
                            ? `2px solid ${accent}`
                            : active
                              ? `2px solid ${accent}`
                              : `2px solid ${inactiveBd}`,
                          bgcolor: done ? accent : 'transparent',
                          color: done ? '#0a0a0a' : active ? accent : inactiveFg,
                        }}
                      >
                        {done ? <CheckCircleIcon sx={{ fontSize: 22 }} /> : i + 1}
                      </Box>
                      {i === 0 && (
                        <Box
                          sx={{
                            width: { xs: 40, sm: 56 },
                            height: 2,
                            mx: 0.75,
                            borderRadius: 1,
                            bgcolor:
                              step >= 1
                                ? accent
                                : isDark
                                  ? alpha('#fff', 0.12)
                                  : alpha('#000', 0.1),
                            alignSelf: 'center',
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Body */}
          <Box
            data-quick-book-scroll
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 2,
              py: 2,
              '& .MuiFormControl-root': { overflow: 'visible' },
            }}
          >
            {step === 0 && (
              <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      filter: aiCheckinOpen ? 'blur(8px)' : 'none',
                      opacity: aiCheckinOpen ? 0.55 : 1,
                      pointerEvents: aiCheckinOpen ? 'none' : 'auto',
                      transition: 'opacity 0.25s ease, filter 0.25s ease',
                    }}
                  >
                    <GuestDetailsStep
                      guestTitle={guestTitle}
                      guestFirstName={guestFirstName}
                      guestMiddleName={guestMiddleName}
                      guestLastName={guestLastName}
                      onGuestTitleChange={setGuestTitle}
                      onGuestFirstNameChange={setGuestFirstName}
                      onGuestMiddleNameChange={setGuestMiddleName}
                      onGuestLastNameChange={setGuestLastName}
                      guestEmail={guestEmail}
                      guestPhone={guestPhone}
                      guestPhoneCountryCode={guestPhoneCountryCode}
                      guestAlternatePhone={guestAlternatePhone}
                      guestAlternatePhoneCountryCode={guestAlternatePhoneCountryCode}
                      onGuestEmailChange={setGuestEmail}
                      onGuestPhoneChange={setGuestPhone}
                      onGuestPhoneCountryCodeChange={handleGuestPhoneCountryCodeChange}
                      onSelectedGuestIdChange={setSelectedGuestId}
                      selectedGuestId={selectedGuestId}
                      onGuestAlternatePhoneChange={setGuestAlternatePhone}
                      onGuestAlternatePhoneCountryCodeChange={handleGuestAlternatePhoneCountryCodeChange}
                      guestAddress={guestAddress}
                      guestCity={guestCity}
                      guestState={guestState}
                      guestCountry={guestCountry}
                      guestPincode={guestPincode}
                      onGuestAddressChange={setGuestAddress}
                      onGuestCityChange={setGuestCity}
                      onGuestStateChange={setGuestState}
                      onGuestCountryChange={setGuestCountry}
                      onGuestPincodeChange={setGuestPincode}
                      guestDOB={guestDOB}
                      guestGender={guestGender}
                      onGuestDOBChange={setGuestDOB}
                      onGuestGenderChange={setGuestGender}
                      guestETA={guestETA}
                      guestNote={guestNote}
                      bookingReference={bookingReference}
                      remarks={remarks}
                      onGuestETAChange={setGuestETA}
                      onGuestNoteChange={setGuestNote}
                      onBookingReferenceChange={setBookingReference}
                      uploadDocumentType={uploadDocumentType}
                      onUploadDocumentChange={setUploadDocumentType}
                      documentIdNumber={documentIdNumber}
                      onDocumentIdNumberChange={setDocumentIdNumber}
                      onRemarksChange={setRemarks}
                      purposeOfStay={purposeOfStay}
                      purposeOfStayNote={purposeOfStayNote}
                      onPurposeOfStayChange={setPurposeOfStay}
                      onPurposeOfStayNoteChange={setPurposeOfStayNote}
                      passportFrontId={passportFrontId}
                      passportBackId={passportBackId}
                      onPassportFrontIdChange={setPassportFrontId}
                      onPassportBackIdChange={setPassportBackId}
                      hotelCountry={hotelCountry}
                      foreignGuestRoomNumber={
                        selectedRooms[0]?.roomNumber || context?.roomNumber || ''
                      }
                      foreignGuestCheckInDate={checkInDate}
                      foreignGuestDetails={foreignGuestDetails}
                      onForeignGuestDetailsChange={setForeignGuestDetails}
                      isWalkIn={true}
                      inDrawer
                      hideGuestInformationHeading
                      onValidationChange={setIsGuestDetailsValid}
                      onValidateRequest={(fn) => {
                        validateGuestDetailsRef.current = fn;
                      }}
                      headingActions={
                        <Box sx={{ width: '100%' }}>
                          <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                            <Grid item xs={12} sm={6} sx={{ overflow: 'visible' }}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="Meal plan"
                                value={
                                  availableMealPlans.some((o) => o.value === mealPlanCode)
                                    ? mealPlanCode
                                    : availableMealPlans[0]?.value || mealPlanCode || 'EP'
                                }
                                onChange={(e) =>
                                  setMealPlanCode(
                                    String(e.target.value || '')
                                      .trim()
                                      .toUpperCase()
                                  )
                                }
                                disabled={loadingRoom}
                                SelectProps={{
                                  MenuProps: drawerSelectMenuProps,
                                  renderValue: (selected) => {
                                    const plans = availableMealPlans.length
                                      ? availableMealPlans
                                      : [{ value: 'EP', label: 'EP' }];
                                    const opt = plans.find((o) => o.value === selected);
                                    return opt?.label || selected || 'EP';
                                  },
                                }}
                                sx={{
                                  ...quickBookOutlinedFieldSx,
                                  '& .MuiInputBase-root': { minHeight: 36 },
                                }}
                              >
                                {(availableMealPlans.length
                                  ? availableMealPlans
                                  : [{ value: 'EP', label: 'EP' }]
                                ).map((o) => (
                                  <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Amount / night"
                                type="number"
                                value={nightRateAmount}
                                onChange={(e) => setNightRateAmount(e.target.value)}
                                disabled={loadingRoom}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">{selectedCurrencySymbol}</InputAdornment>
                                  ),
                                }}
                                inputProps={{ min: 0, step: 1 }}
                                sx={{ '& .MuiInputBase-root': { minHeight: 36 } }}
                              />
                            </Grid>
                          </Grid>
                          <Grid container spacing={1.5} alignItems="center">
                            <Grid item xs={12} md={6}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  flexWrap: 'wrap',
                                  minWidth: 0,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 800,
                                    letterSpacing: '-0.01em',
                                    color: isDark ? '#fff' : '#0f172a',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {checkInDate.format('DD MMM YYYY')}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: 36,
                                    borderRadius: 1.25,
                                    border: `1px solid ${isDark ? alpha('#fff', 0.18) : alpha('#000', 0.14)}`,
                                    overflow: 'hidden',
                                    bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.03),
                                    minWidth: 120,
                                    flexShrink: 0,
                                  }}
                                >
                                  <IconButton
                                    type="button"
                                    size="small"
                                    onClick={() => setSelectedDays((d) => Math.max(1, d - 1))}
                                    disabled={selectedDays <= 1 || loadingRoom}
                                    aria-label="Decrease nights"
                                    sx={{
                                      borderRadius: 0,
                                      flex: 1,
                                      color: isDark ? '#fff' : '#0f172a',
                                      py: 0.5,
                                      '&:hover': { bgcolor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06) },
                                    }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <Typography
                                    sx={{
                                      flex: 1,
                                      textAlign: 'center',
                                      fontWeight: 800,
                                      fontSize: '0.95rem',
                                      borderLeft: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#000', 0.1)}`,
                                      borderRight: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#000', 0.1)}`,
                                      py: 0.5,
                                      minWidth: 32,
                                    }}
                                  >
                                    {selectedDays}
                                  </Typography>
                                  <IconButton
                                    type="button"
                                    size="small"
                                    onClick={() => setSelectedDays((d) => Math.min(30, d + 1))}
                                    disabled={selectedDays >= 30 || loadingRoom}
                                    aria-label="Increase nights"
                                    sx={{
                                      borderRadius: 0,
                                      flex: 1,
                                      color: isDark ? '#fff' : '#0f172a',
                                      py: 0.5,
                                      '&:hover': { bgcolor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06) },
                                    }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                {loadingRoom && <CircularProgress size={20} thickness={5} />}
                              </Box>
                            </Grid>
                            {canManageAiCheckin && (
                              <Grid item xs={12} sm={6} md={3}>
                                <AiGradientButton
                                  fullWidth
                                  size="small"
                                  onClick={() => setAiCheckinOpen(true)}
                                >
                                  AI Check-in
                                </AiGradientButton>
                              </Grid>
                            )}
                            <Grid item xs={12} sm={6} md={3} sx={{ ml: { md: 'auto' } }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.75,
                                  alignItems: 'stretch',
                                }}
                              >
                                <CompanyDetailsHeadingActions
                                  hasCompanyDetails={hasCompanyDetails}
                                  companyName={companyDetails.companyName}
                                  isDark={isDark}
                                  accent={accent}
                                  chipSeparator="·"
                                  chipSize="small"
                                  fullWidthAdd
                                  addLabel="Add company"
                                  onEdit={handleOpenCompanyDialog}
                                  onRemove={handleRemoveCompanyDetailsForBooking}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                          {roomLoadError ? (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                              {roomLoadError}
                            </Typography>
                          ) : null}
                        </Box>
                      }
                    />
                  </Box>
                  {canManageAiCheckin && (
                    <WalkInAiCheckinOverlay
                      open={aiCheckinOpen}
                      onClose={() => setAiCheckinOpen(false)}
                      onSuccess={(guest, previews) => {
                        setSelectedGuestId(null);
                        applyAiGuestFields(guest, previews);
                        setAiCheckinOpen(false);
                      }}
                      isDark={isDark}
                    />
                  )}
              </Box>
            )}

            {step === 1 && (
              <ReviewStep
                checkInDate={checkInDate}
                checkOutDate={checkOutDate}
                nights={nights}
                rooms={selectedRooms}
                guestName={`${guestFirstName} ${guestLastName}`.trim()}
                guestEmail={guestEmail}
                guestPhone={guestPhone}
                guestAddress={guestAddress}
                gstIncluded={effectiveGstIncluded}
                taxInclusive={taxInclusive}
                allowDiscount={allowDiscount}
                discountMode={discountMode}
                discountPercentage={discountPercentage}
                discountAmount={discountAmount}
                maxDiscountAmount={priceBreakdown?.priceBeforeDiscount || 0}
                autoGstEnabled={autoGstEnabled}
                onGstIncludedChange={setGstIncluded}
                onTaxInclusiveChange={setTaxInclusive}
                onAllowDiscountChange={setAllowDiscount}
                onDiscountModeChange={setDiscountMode}
                onDiscountPercentageChange={setDiscountPercentage}
                onDiscountAmountChange={setDiscountAmount}
                allowExtraCharges={allowExtraCharges}
                extraChargesAmount={extraChargesAmount}
                extraChargesNote={extraChargesNote}
                extraChargesItems={extraChargesItems}
                extraChargesBaseAmount={extraChargesBaseAmount}
                extraChargesGstAmount={extraChargesGstAmount}
                onAllowExtraChargesChange={setAllowExtraCharges}
                onExtraChargesAmountChange={setExtraChargesAmount}
                onExtraChargesNoteChange={setExtraChargesNote}
                onExtraChargesItemsChange={setExtraChargesItems}
                onExtraChargesBaseAmountChange={setExtraChargesBaseAmount}
                onExtraChargesGstAmountChange={setExtraChargesGstAmount}
                priceBreakdown={priceBreakdown}
                bookingType={bookingType}
                isWalkIn={true}
                hasTaxRules={hasTaxRules}
                paymentType={paymentType}
                paymentAmount={paymentAmount}
                onPaymentTypeChange={setPaymentType}
                onPaymentAmountChange={(v) => setPaymentAmount(toCurrencyAmount(v))}
                paymentMethod={paymentMethod}
                referenceNumber={referenceNumber}
                paymentDocument={paymentDocument}
                onPaymentMethodChange={setPaymentMethod}
                onReferenceNumberChange={setReferenceNumber}
                onPaymentDocumentChange={setPaymentDocument}
                collectionDate={collectionDate}
                onCollectionDateChange={setCollectionDate}
                flatReviewChrome
                inDrawer
              />
            )}
          </Box>

          {/* Footer — half-width buttons */}
          <Box
            sx={{
              p: 2,
              pt: 1.5,
              borderTop: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#000', 0.08)}`,
              bgcolor: isDark ? alpha('#000', 0.15) : alpha(accent, 0.04),
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={step === 0 ? onClose : handleBack}
                  disabled={isSubmitting}
                  sx={{ py: 1.25, fontWeight: 700 }}
                >
                  {step === 0 ? 'Cancel' : 'Back'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                {step === 0 ? (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleNext}
                    disabled={loadingRoom || !selectedRooms.length}
                    sx={{
                      py: 1.25,
                      fontWeight: 700,
                      bgcolor: accent,
                      color: '#000',
                      '&:hover': { bgcolor: accent, opacity: 0.92 },
                    }}
                  >
                    Next · Review
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handleSubmit()}
                    disabled={isSubmitting}
                    sx={{
                      py: 1.25,
                      fontWeight: 700,
                      bgcolor: accent,
                      color: '#000',
                      '&:hover': { bgcolor: accent, opacity: 0.92 },
                    }}
                  >
                    {isSubmitting ? <CircularProgress size={22} color="inherit" /> : 'Confirm booking'}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Walk-in style success — scoped inside quick-book drawer (same actions as WalkInForm) */}
        <Dialog
          open={successDialogOpen}
          onClose={bookingData?.processing ? undefined : handleCloseSuccessDialog}
          maxWidth="sm"
          fullWidth
          container={drawerModalContainer}
          sx={successModalSx}
          BackdropProps={{ sx: scopedBackdropSx }}
          PaperProps={{ sx: successWalkInPaperSx }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 1.5,
              pt: 2.5,
              px: 2.5,
              borderBottom: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              background: isDark
                ? `linear-gradient(135deg, rgba(76, 225, 182, 0.05) 0%, rgba(58, 184, 150, 0.05) 100%)`
                : `linear-gradient(135deg, rgba(76, 225, 182, 0.03) 0%, rgba(58, 184, 150, 0.03) 100%)`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {bookingData?.processing ? (
                <CircularProgress size={24} sx={{ color: '#4ce1b6' }} />
              ) : (
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, #4ce1b6 0%, #3ab896 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(76, 225, 182, 0.4)',
                  }}
                >
                  <CheckCircleIcon sx={{ color: '#000', fontSize: 20 }} />
                </Box>
              )}
              <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600, fontSize: '1rem' }}>
                {bookingData?.processing
                  ? 'Processing Booking...'
                  : successGroupBookingCode
                    ? `Group Booking Confirmed! (${bookingData?.bookings?.length || 0})`
                    : bookingData?.bookings && bookingData.bookings.length > 1
                      ? `Bookings Confirmed! (${bookingData.bookings.length})`
                      : 'Booking Confirmed!'}
              </Typography>
            </Box>
            {!bookingData?.processing && (
              <IconButton
                onClick={handleCloseSuccessDialog}
                size="small"
                sx={{
                  color: isDark ? drawerTheme.subtleText : '#666',
                  '&:hover': {
                    backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </DialogTitle>
          <DialogContent sx={{ px: 2.5, py: 2 }}>
            {bookingData?.processing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                <CircularProgress size={48} sx={{ color: '#4ce1b6', mb: 2 }} />
                <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', textAlign: 'center', fontSize: '0.875rem' }}>
                  Please wait while we process your booking...
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', mt: 0.5, textAlign: 'center', fontSize: '0.75rem' }}>
                  This may take a few moments
                </Typography>
              </Box>
            ) : (
              bookingData && (
                <Box>
                  {bookingData.bookings && bookingData.bookings.length > 1 ? (
                    <Box>
                      {successGroupBookingCode ? (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.25,
                            mb: 1.25,
                            borderRadius: 1.5,
                            backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
                            border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.28)'}`,
                          }}
                        >
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: isDark ? drawerTheme.subtleText : '#666' }}>
                            Group booking
                          </Typography>
                          <Typography sx={{ color: '#4ce1b6', fontWeight: 800, fontSize: '0.95rem' }}>
                            {successGroupBookingCode}
                          </Typography>
                          <Typography sx={{ mt: 0.25, fontSize: '0.7rem', color: isDark ? drawerTheme.subtleText : '#666' }}>
                            One invoice covers all member bookings
                          </Typography>
                        </Paper>
                      ) : null}
                      <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666', mb: 1.5, fontSize: '0.75rem', textAlign: 'center' }}>
                        {bookingData.message || `Successfully created ${bookingData.bookings.length} bookings`}
                      </Typography>
                      <Grid container spacing={1.5}>
                        {bookingData.bookings.map((booking, index) => (
                          <Grid item xs={12} key={booking.booking_id || index}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 1.5,
                                backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f8f9fa',
                                borderRadius: 1.5,
                                border: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
                                boxShadow: `0 2px 8px rgba(76, 225, 182, 0.15), inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)'}`,
                              }}
                            >
                              <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                                Booking {index + 1}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#4ce1b6', fontWeight: 600, fontSize: '0.875rem' }}>
                                {booking.booking_code || booking.booking_id || 'N/A'}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                      <Paper
                        elevation={0}
                        sx={{
                          mt: 1.5,
                          p: 1.5,
                          backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f8f9fa',
                          borderRadius: 1.5,
                          border: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
                          boxShadow: `0 2px 8px rgba(76, 225, 182, 0.1)`,
                        }}
                      >
                        <Grid container spacing={1.5}>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                              Guest
                            </Typography>
                            <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                              {walkInSuccessGuestLine}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                              Phone
                            </Typography>
                            <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                              {guestPhone || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                              Rooms
                            </Typography>
                            <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                              {selectedRooms.length}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                              Amount
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4ce1b6', fontWeight: 600, fontSize: '0.8rem' }}>
                              {selectedCurrencySymbol}{toCurrencyAmount(priceBreakdown.total).toFixed(2)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Box>
                  ) : bookingData.bookings && bookingData.bookings.length === 1 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f8f9fa',
                        borderRadius: 1.5,
                        border: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
                        boxShadow: `0 2px 8px rgba(76, 225, 182, 0.15), inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)'}`,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                        Booking Code
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4ce1b6', fontWeight: 600, fontSize: '0.875rem', mb: 1.5 }}>
                        {bookingData.bookings[0].booking_code || bookingData.bookings[0].booking_id || 'N/A'}
                      </Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                            Guest
                          </Typography>
                          <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                            {walkInSuccessGuestLine}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                            Phone
                          </Typography>
                          <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                            {guestPhone || '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                            Rooms
                          </Typography>
                          <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                            {selectedRooms.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                            Amount
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#4ce1b6', fontWeight: 600, fontSize: '0.8rem' }}>
                            {selectedCurrencySymbol}{priceBreakdown.total.toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f8f9fa',
                        borderRadius: 1.5,
                        border: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
                        boxShadow: `0 2px 8px rgba(76, 225, 182, 0.15), inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)'}`,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                        Booking Code
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4ce1b6', fontWeight: 600, fontSize: '0.875rem', mb: 1.5 }}>
                        {bookingData.booking_code || bookingData.code || bookingData.booking_id || 'N/A'}
                      </Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                            Guest
                          </Typography>
                          <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                            {walkInSuccessGuestLine}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                            Phone
                          </Typography>
                          <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                            {guestPhone || '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                            Rooms
                          </Typography>
                          <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 500, fontSize: '0.8rem' }}>
                            {selectedRooms.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                            Amount
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#4ce1b6', fontWeight: 600, fontSize: '0.8rem' }}>
                            {selectedCurrencySymbol}{priceBreakdown.total.toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                </Box>
              )
            )}
          </DialogContent>
          {!bookingData?.processing && (
            <DialogActions
              sx={{
                p: 2,
                pt: 1.25,
                flexDirection: 'column',
                gap: 0,
                alignItems: 'stretch',
                borderTop: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              }}
            >
              <BookingConfirmedActionsContent
                isDark={isDark}
                accent={accent}
                successActionLoading={successActionLoading}
                onDownloadInvoice={handleDownloadInvoice}
                onDownloadVoucher={handleDownloadVoucher}
                onSendWhatsApp={handleSendWhatsApp}
                onSendEmail={handleSendEmail}
                onDone={handleCloseSuccessDialog}
                invoiceButtonLabel={
                  successGroupBookingCode ? 'Download group invoice' : 'Download invoice'
                }
                voucherButtonLabel={
                  successGroupBookingCode ? 'Download group voucher' : 'Download voucher'
                }
                showPaymentLink={showPaymentLinkOnSuccess}
                paymentLinkBookingId={successPaymentLinkBookingId}
                paymentLinkBooking={successPaymentLinkBooking}
                paymentLinkAmount={successPaymentLinkAmount}
                canSendPaymentLinkEmail={canSendPaymentLinkEmail}
                canSendPaymentLinkWhatsApp={canSendPaymentLinkWhatsApp}
                onPaymentLinkSent={(msg) => showAlert(msg, 'Success', 'success')}
              />
            </DialogActions>
          )}
        </Dialog>

        <AlertDialog
          open={alertDialogOpen}
          onClose={() => setAlertDialogOpen(false)}
          title={alertTitle}
          message={alertMessage}
          severity={alertSeverity}
          container={drawerModalContainer}
          sx={scopedModalSx}
          BackdropProps={{ sx: scopedBackdropSx }}
          PaperProps={{ sx: scopedPaperSx }}
        />
      </Drawer>

      <Drawer
        anchor="right"
        open={companyDialogOpen}
        onClose={() => setCompanyDialogOpen(false)}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: companyDrawerPaperSx,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              ...getBookingDrawerHeaderSx(isDark),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600 }}>
                Company details
              </Typography>
            </Box>
            <IconButton
              onClick={() => setCompanyDialogOpen(false)}
              sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, ...drawerOverlays.fieldShellSx }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Tax ID type"
                  value={companyDetails.taxIdType}
                  onChange={(e) => setCompanyDetails((p) => ({ ...p, taxIdType: e.target.value }))}
                  SelectProps={{ MenuProps: drawerSelectMenuProps }}
                  sx={companyFieldSx}
                >
                  {corporateTaxIdOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Tax identification number"
                  value={companyDetails.taxIdNumber}
                  onChange={(e) => setCompanyDetails((p) => ({ ...p, taxIdNumber: e.target.value }))}
                  error={Boolean(companyErrors.taxIdNumber)}
                  helperText={companyErrors.taxIdNumber || ''}
                  sx={companyFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company name"
                  value={companyDetails.companyName}
                  onChange={(e) => setCompanyDetails((p) => ({ ...p, companyName: e.target.value }))}
                  error={Boolean(companyErrors.companyName)}
                  helperText={companyErrors.companyName || ''}
                  sx={companyFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company phone"
                  value={companyDetails.companyPhone}
                  onChange={(e) => setCompanyDetails((p) => ({ ...p, companyPhone: e.target.value }))}
                  sx={companyFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company email"
                  value={companyDetails.companyEmail}
                  onChange={(e) => setCompanyDetails((p) => ({ ...p, companyEmail: e.target.value }))}
                  error={Boolean(companyErrors.companyEmail)}
                  helperText={companyErrors.companyEmail || ''}
                  sx={companyFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={companyDetails.companyAddress}
                  onChange={(e) => setCompanyDetails((p) => ({ ...p, companyAddress: e.target.value }))}
                  multiline
                  rows={2}
                  sx={companyFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={companyCountries}
                  getOptionLabel={(option) => option?.name || ''}
                  filterOptions={countryFilterOptions}
                  slotProps={drawerOverlays.autocompleteSlotProps}
                  value={selectedCompanyCountryObj}
                  onChange={(event, newValue) => {
                    setCompanyDetails((p) => ({
                      ...p,
                      companyCountry: newValue?.name || '',
                      companyState: '',
                      companyCity: '',
                    }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Country" sx={companyFieldSx} />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={companyStates}
                  getOptionLabel={(option) => option?.name || ''}
                  filterOptions={stateFilterOptions}
                  slotProps={drawerOverlays.autocompleteSlotProps}
                  value={selectedCompanyStateObj}
                  onChange={(event, newValue) => {
                    setCompanyDetails((p) => ({
                      ...p,
                      companyState: newValue?.name || '',
                      companyCity: '',
                    }));
                  }}
                  disabled={!selectedCompanyCountryObj}
                  renderInput={(params) => <TextField {...params} label="State" sx={companyFieldSx} />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={companyCities}
                  getOptionLabel={(option) => option || ''}
                  filterOptions={cityFilterOptions}
                  slotProps={drawerOverlays.autocompleteSlotProps}
                  value={companyCities.find((city) => city === companyDetails.companyCity) || null}
                  onChange={(event, newValue) => {
                    setCompanyDetails((p) => ({ ...p, companyCity: newValue || '' }));
                  }}
                  disabled={!selectedCompanyStateObj}
                  renderInput={(params) => <TextField {...params} label="City" sx={companyFieldSx} />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Pincode"
                  value={companyDetails.companyPincode}
                  onChange={(e) => setCompanyDetails((p) => ({ ...p, companyPincode: e.target.value }))}
                  sx={companyFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert
                  severity="info"
                  sx={{
                    border: 'none',
                    bgcolor: isDark ? alpha('#fff', 0.06) : alpha(accent, 0.1),
                    color: isDark ? alpha('#fff', 0.85) : 'text.primary',
                    '& .MuiAlert-icon': { color: isDark ? accent : theme.palette.info.main },
                  }}
                >
                  Optional corporate billing — same as walk-in flow.
                </Alert>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ p: 1.5, display: 'flex', gap: 1.5, ...getBookingDrawerFooterSx(isDark) }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setCompanyDialogOpen(false)}
              sx={getBookingDrawerOutlinedButtonSx(isDark)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleCompanySave}
              sx={getBookingDrawerPrimaryButtonSx(isDark)}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Drawer>
    </LocalizationProvider>
  );
};

export default QuickBookingDrawer;
